import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";

/**
 * OAuth authorization codes are single-use. React 18 StrictMode remounts the
 * component once in dev, and a non-memoized parent callback can retrigger
 * effects—both would fire a second /external-token call and invalidate storage.
 * Deduplicate by `code` at module scope so every mount shares one fetch.
 */
const tokenExchangePromiseByCode = new Map();

function fetchExternalToken(code, provider, requestBody) {
  let p = tokenExchangePromiseByCode.get(code);
  if (p) return p;

  console.log("Fetching token with code");
  p = fetch(`${process.env.REACT_APP_API_BASE_URL}/external-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  })
    .then((response) => {
      console.log("Token response status:", response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json();
    })
    .finally(() => {
      tokenExchangePromiseByCode.delete(code);
    });

  tokenExchangePromiseByCode.set(code, p);
  return p;
}

/**
 * Component to handle the OAuth redirect callback.
 * Exchanges the received authorization code for tokens,
 * and then calls the parent-provided callback to complete login.
 */

function AuthCallback({ handleAuthCallback, isAuthenticated }) {
  const [error, setError] = useState(null); // Store any error during the process
  const [processed, setProcessed] = useState(false); // Flag to avoid processing more than once
  const handleAuthCallbackRef = useRef(handleAuthCallback);
  handleAuthCallbackRef.current = handleAuthCallback;

  useEffect(() => {
    console.log("AuthCallback mounted");

    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get("code"); // OAuth code returned by Google
    const provider = searchParams.get("provider") || "google"; // Default to Google

    if (!code) {
      console.log("No code found in URL");
      setProcessed(true);
      return;
    }

    const storedUserInfo = localStorage.getItem("userInfo");

    const requestBody = {
      code,
      provider,
      client_id: "google-auth-demo",
    };

    if (storedUserInfo) {
      requestBody.userInfo = JSON.parse(storedUserInfo);
    }

    let cancelled = false;

    fetchExternalToken(code, provider, requestBody)
      .then((data) => {
        if (cancelled) return;
        console.log("Token data received:", data);

        if (data?.oidc_id_token) {
          localStorage.setItem("oidcIdToken", data.oidc_id_token);
        }

        const params = new URLSearchParams();
        params.set("token", data.access_token);

        const userInfoAfter = localStorage.getItem("userInfo");
        if (userInfoAfter) {
          params.set("userData", userInfoAfter);
        } else if (data.email) {
          const userData = { email: data.email, provider: provider };
          params.set("userData", JSON.stringify(userData));
          console.log("Setting user data:", userData);
        }

        const success = handleAuthCallbackRef.current(params);
        console.log("Auth callback result:", success);
        setProcessed(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error exchanging code for token:", err);
        setError(err.message);
        setProcessed(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // While the authentication process is running
  if (!processed) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Processing Authentication...</h2>
        <p>URL: {window.location.href}</p>
        <p>Processing: {processed ? "Done" : "In Progress"}</p>
      </div>
    );
  }

  // Show error message if something went wrong
  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Authentication Error</h2>
        <p style={{ color: "red" }}>{error}</p>
        <button onClick={() => (window.location.href = "/")}>
          Return to Home
        </button>
      </div>
    );
  }

  // If successfully authenticated, redirect to dashboard
  if (isAuthenticated) {
    console.log("User is authenticated, redirecting to dashboard");
    return <Navigate to="/dashboard" />;
  }

  // If authentication failed, redirect to home
  console.log("Not authenticated, redirecting to home");
  return <Navigate to="/" />;
}

export default AuthCallback;
